export type Region = 'us' | 'global';

export type Platform = 'ios' | 'android' | 'unknown';

interface AppIds {
  iosUs: string;
  iosGlobal: string;
  androidUs: string;
  androidGlobal: string;
}

const APP_IDS: AppIds = {
  iosUs: '584557117',
  iosGlobal: '585858244',
  androidUs: 'com.rhapsody',
  androidGlobal: 'com.rhapsody.napster',
};

// iPadOS Safari reports a Mac user agent; touch support gives it away
export const detectPlatform = (
  userAgent: string,
  maxTouchPoints = 0,
): Platform => {
  const ua = userAgent.toLowerCase();
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  return 'unknown';
};

export const detectRegion = (): Region => {
  const urlParams = new URLSearchParams(window.location.search);
  const regionParam = urlParams.get('region');
  if (regionParam === 'us' || regionParam === 'global') {
    return regionParam;
  }

  const regionEnv = (window as any).__APP_REGION__;
  if (regionEnv === 'us' || regionEnv === 'global') {
    return regionEnv;
  }

  return 'global';
};

export const getAppStoreUrl = (platform: Platform, region: Region): string => {
  if (platform === 'ios') {
    const appId = region === 'us' ? APP_IDS.iosUs : APP_IDS.iosGlobal;
    return `https://apps.apple.com/app/id${appId}`;
  }
  if (platform === 'android') {
    const packageName =
      region === 'us' ? APP_IDS.androidUs : APP_IDS.androidGlobal;
    return `https://play.google.com/store/apps/details?id=${packageName}`;
  }
  return '';
};

// The store page for this phone in the visitor's region, or null on a computer
export const appStoreUrlForDevice = (): string | null => {
  const override = new URLSearchParams(window.location.search).get(
    'testMobile',
  );
  const platform =
    override === 'ios' || override === 'android'
      ? override
      : detectPlatform(navigator.userAgent, navigator.maxTouchPoints);
  return platform === 'unknown'
    ? null
    : getAppStoreUrl(platform, detectRegion());
};

// Android WebViews mark themselves with "; wv)"; social apps add their own tokens
const IN_APP_BROWSER =
  /;\s*wv\)|FBAN|FBAV|FB_IAB|Instagram|Snapchat|musical_ly|TikTok/i;

export const isInAppBrowser = (userAgent: string): boolean =>
  IN_APP_BROWSER.test(userAgent);

const PLAY_DETAILS =
  /^https:\/\/play\.google\.com\/store\/apps\/details\?id=([\w.]+)$/;
const PLAY_STORE_PACKAGE = 'com.android.vending';
const PLAY_FALLBACK_MS = 2500;

// Pinned to the Play Store app; browsers that cannot launch it load the web page instead
export const playStoreIntentUrl = (packageName: string): string =>
  `intent://details?id=${packageName}#Intent;scheme=market;package=${PLAY_STORE_PACKAGE};` +
  `S.browser_fallback_url=${encodeURIComponent(
    `https://play.google.com/store/apps/details?id=${packageName}`,
  )};end`;

// Only Chrome hands https Play links to the Play Store app, so other Android browsers get an intent link
export const openAppStore = (storeUrl: string): void => {
  const play = PLAY_DETAILS.exec(storeUrl);
  if (!play || isInAppBrowser(navigator.userAgent)) {
    window.location.assign(storeUrl);
    return;
  }

  // a prompt, the store opening or the tab hiding means the browser took over, so no fallback
  const cancel = () => {
    window.clearTimeout(timer);
    window.removeEventListener('blur', cancel);
    window.removeEventListener('pagehide', cancel);
    document.removeEventListener('visibilitychange', cancel);
  };
  const timer = window.setTimeout(() => {
    cancel();
    if (!document.hidden) window.location.assign(storeUrl);
  }, PLAY_FALLBACK_MS);
  window.addEventListener('blur', cancel);
  window.addEventListener('pagehide', cancel);
  document.addEventListener('visibilitychange', cancel);

  window.location.assign(playStoreIntentUrl(play[1]));
};

export const getAppDeepLink = (
  platform: Platform,
  region: Region,
  currentUrl: string,
): string => {
  if (platform === 'ios') {
    return currentUrl;
  }
  if (platform === 'android') {
    const packageName =
      region === 'us' ? APP_IDS.androidUs : APP_IDS.androidGlobal;
    const urlPath = currentUrl.replace(/^https?:\/\/[^/]+/, '');
    const encodedFallback = encodeURIComponent(currentUrl);
    return `intent:${urlPath}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodedFallback};end`;
  }
  return currentUrl;
};

export const isMobileDevice = (userAgent: string): boolean => {
  const platform = detectPlatform(userAgent);
  return platform === 'ios' || platform === 'android';
};
